<template>
  <div class="side-panel">
    <v-layout>
      <search-bar @search="filteredBoluses = filterTable(boluses, $event)" :columns="headers"></search-bar>
      <v-spacer></v-spacer>
      <v-btn
          slot="action"
          @click="createBolusModalVisible = true"
          class="secondary align-self-center"
          light>
            {{$t('bolusDetails.createBolus')}}
        </v-btn>
      <div class="align-self-center" v-if="
        $store.state.user !== null &&
          ([RoleTypes.SuperAdmin, RoleTypes.CompanyAdmin].includes($store.state.user.role_type)) 
        "
      >
        <input id="fileUpload" type="file" accept=".csv" @change="fileSelectOnChange" hidden>
        <v-btn class="secondary align-self-center" @click="chooseFiles()">{{$t('bolusDetails.uploadBoluses')}}</v-btn>
      </div>
    </v-layout>
    <div class="align-self-center" v-if="
      $store.state.user !== null &&
        ([RoleTypes.SuperAdmin, RoleTypes.CompanyAdmin].includes($store.state.user.role_type)) 
      "
    >
      <v-layout>
        <v-spacer></v-spacer>
        <date-time-picker-with-zone
          v-if="updateActivationDate.isInProgress"
          ref="UpdateActivationDatePicker"
          class="align-self-center"
          @dateChanged="newDate => (updateActivationDate.activationDate = newDate)"
          label="Activation date:"
          :actDate="updateActivationDate.activationDate"
        />
        <v-btn
          v-if="!updateActivationDate.isInProgress && !updateCalibration.isInProgress"
          slot="action"
          @click="setActDateProgress(true)"
          class="secondary align-self-center"
          light>
            {{$t('bolusDetails.updateActDate')}}
        </v-btn>
        <v-btn
          v-if="updateActivationDate.isInProgress"
          :disabled="actDateUpdateIsDisabled()"
          slot="action"
          @click="saveNewActDates()"
          class="secondary align-self-center"
          light>
            {{$t('actions.save')}}
        </v-btn>
        <v-btn
          v-if="updateActivationDate.isInProgress"
          slot="action"
          @click="setActDateProgress(false)"
          class="primary align-self-center"
          light>
            {{$t('actions.cancel')}}
        </v-btn>
        <v-btn
          v-if="!updateCalibration.isInProgress"
          slot="action"
          @click="setCalibrationProgress(true)"
          class="secondary align-self-center"
          light>
            {{$t('bolusDetails.updateCalibration')}}
        </v-btn>
        <date-time-picker-with-zone
          v-if="updateCalibration.isInProgress"
          ref="UpdateCalibrationFromDatePicker"
          class="align-self-center"
          @dateChanged="newDate => (updateCalibration.from = newDate)"
          label="From date:"
          :actDate="updateCalibration.from"
        />
        <date-time-picker-with-zone
          ref="UpdateCalibrationToDatePicker"
          v-if="updateCalibration.isInProgress"
          class="align-self-center"
          @dateChanged="newDate => (updateCalibration.to = newDate)"
          label="To date:"
          :actDate="updateCalibration.to"
        />
        <v-btn
          v-if="updateCalibration.isInProgress"
          :disabled="calibrationIsDisabled()"
          slot="action"
          @click="saveCalibration()"
          class="secondary align-self-center"
          light>
            {{$t('actions.save')}}
        </v-btn>
        <v-btn
          v-if="updateCalibration.isInProgress"
          slot="action"
          @click="setCalibrationProgress(false)"
          class="primary align-self-center"
          light>
            {{$t('actions.cancel')}}
        </v-btn>
      </v-layout>
    </div>
    <v-data-table
      :headers="headers"
      :items="filteredBoluses"
      :loading="loading && error === null"
      v-model="selected"
      item-key="bolus_id"
      :select-all="updateActivationDate.isInProgress || updateCalibration.isInProgress"
    >
        <template slot="items" slot-scope="props">
          <tr @click="openModal(props)">
            <td v-if="updateActivationDate.isInProgress || updateCalibration.isInProgress" class="text-xs-center">
              <v-checkbox
                v-model="props.selected"
                primary
                hide-details
              ></v-checkbox>
            </td>
            <td class="text-xs-center">{{ props.item.bolus_id }}</td>
            <td class="text-xs-center">{{ props.item.bolus_name }}</td>
            <td class="text-xs-center">{{ props.item.imei }}</td>
            <td class="text-xs-center">{{ props.item.deveui }}</td>
            <td class="text-xs-center">{{ props.item.activation_date ? $moment(props.item.activation_date).format($dateFormat) : '' }}</td>
            <td v-if="$store.state.user.role_type === RoleTypes.SuperAdmin" class="text-xs-center">{{ props.item.company.company_id }}</td>
            <td v-if="$store.state.user.role_type === RoleTypes.SuperAdmin" class="text-xs-center">{{ props.item.company.company_name }}</td>
            <td class="text-xs-center">{{ props.item.is_ph ? 'True' : 'False' }}</td>
            <td class="text-xs-center">
              {{ getLatestPhConfig(props.item.bolus_ph_configs, 'four_ph_value') }}
            </td>
            <td class="text-xs-center">
              {{ getLatestPhConfig(props.item.bolus_ph_configs, 'seven_ph_value') }}
            </td>
            <td class="text-xs-center">
              {{ getValidFrom(props.item.bolus_ph_configs) }}
            </td>
            <td>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <span v-on="on">{{ getLastSeen(props.item.last_message) }}</span>
                </template>
                <span style="font-size: 15px">
                  {{ getLastSeenTip(props.item.last_message) }}
                </span>
              </v-tooltip>
            </td>
            <td class="text-xs-center">
              {{ props.item.pending_msg }}
            </td>
        </tr>
        </template>
    </v-data-table>
    <div class="danger-alert" v-if="error"> {{error}} </div>
    <div class="danger-alert" v-for="err in server_errors"> {{err}} </div>
    <v-dialog v-model="createBolusModalVisible" max-width="500px">
      <create-bolus v-on:close="createBolusModalVisible = false; loadData()"></create-bolus>
    </v-dialog>
    <v-dialog v-model="editBolusModalVisible" max-width="500px">
      <edit-bolus v-on:close="editBolusModalVisible = false; loadData()" :clickedId="this.clickedId"></edit-bolus>
    </v-dialog>
  </div>
</template>

<script>
import UtilsService from '@/services/UtilsService'
import EditBolus from '@/components/EditBolus'
import CreateBolus from '@/components/CreateBolus'
import FilterTable from '@/helpers/FilterTable.js'
import store from "@/plugins/store/store";
import BolusApi from "@/api/bolus-api";
import SensorLogApi from "@/api/sensor-log-api";
import {RoleTypes} from "./../constants/role_types";

export default {
  components: {
    EditBolus,
    CreateBolus
  },
  mixins: [FilterTable],
  data () {
    return {
      error: null,
      server_errors: [],
      boluses: [],
      loading: null,
      createBolusModalVisible: false,
      editBolusModalVisible: false,
      clickedId: null, // identify the Bolus to be edited
      filteredBoluses: [],
      bolusesCsv: null,
      updateActivationDate: {
        isInProgress: false,
        bolusIds: [],
        activationDate: null
      },
      updateCalibration: {
        isInProgress: false,
        bolusIds: [],
        from: null,
        to: null
      },
      selected: [],
      RoleTypes: RoleTypes,
      search: null
    }
  },
  computed: {
    headers() {
      let mainHeaders = [
        { text: this.$t('bolusGenerics.bolusId'),
          align: 'center',
          sortable: false,
          value: 'bolus_id'
        },
        { text: this.$t('bolusGenerics.bolusName'),
          value: 'bolus_name',
          align: 'center' },
        { text: 'Imei',
          value: 'imei',
          align: 'center' },
        { text: this.$t('bolusGenerics.deveui'),
          value: 'deveui',
          align: 'center' },
        { text: this.$t('bolusGenerics.activationDate'),
          value: 'activation_date',
          align: 'center' }
      ];

      if (store.state.user.role_type === 'SuperAdmin') {
        mainHeaders = mainHeaders.concat([
          { text: this.$t('bolusGenerics.companyId'),
          value: 'company_id',
          align: 'center' },
          { text: this.$t('bolusGenerics.companyName'),
            value: 'company_name',
            align: 'center' }
        ]);
      }

      mainHeaders = mainHeaders.concat([
        { text: this.$t('bolusGenerics.isPh'),
          value: 'is_ph',
          align: 'center' },
        { text: this.$t('bolusGenerics.pHConfigfourPh'),
          value: 'bolus_ph_configs[0].four_ph_value',
          align: 'center' },
        { text: this.$t('bolusGenerics.pHConfigsevenPh'),
          value: 'bolus_ph_configs[0].seven_ph_value',
          align: 'center' },
        { text: this.$t('bolusGenerics.pHConfigValidFrom'),
          value: 'bolus_ph_configs[0].valid_from',
          align: 'center' },
        { text: this.$t("dashboardView.listHeader.lastSeen"),
          value: 'last_message',
          align: 'center' },
        { text: this.$t("dashboardView.listHeader.pendingMsgs"),
          value: 'pending_msg',
          align: 'center' },
      ]);

      return mainHeaders;
    },
  },
  async mounted () {
    await this.loadData()
    this.filteredBoluses = this.boluses
  },
  methods: {
    getLastSeenTip(dateString) {
      if(!dateString) {
        return "No data";
      }
      return UtilsService.fromUtcToLocalDate(dateString).format("D MMM YYYY HH:mm:ss")
    },
    getValidFrom(configs) {
      const conf = this.getLatestPhConfig(configs, 'valid_from');
      if (!conf) {
        return '';
      }
      return UtilsService.fromUtcToLocalDate(conf).format('YYYY-MM-DD');
    },
    getLastSeen(dateString) {
      if (!dateString) {
        return this.$t("general.never").toUpperCase();
      }

      const dateTime = this.$moment(dateString);

      const now = this.$moment();

      const diff = now - dateTime;

      const diffInDays = Math.ceil(diff / 1000 / 60 / 60 / 24);

      if (diffInDays === 1) {
        const diffInHours = Math.ceil(diff / 1000 / 60 / 60);
        if (diffInHours === 1) {
          const diffInMinutes = Math.ceil(diff / 1000 / 60);
          return `${diffInMinutes} minutes ago`;
        }
        return `${diffInHours} hours ago`;
      }

      return `${diffInDays} days ago`;
    },
    getLatestPhConfig(bolus_ph_configs, key) {
      if (!bolus_ph_configs || bolus_ph_configs.length == 0)
        return '';

      // Find the latest config
      return bolus_ph_configs.reduce((latest, current) => {
        const latestValidFrom = new Date(latest.validFrom);
        const currentValidFrom = new Date(current.validFrom);
        return latestValidFrom > currentValidFrom ? latest : current;
      })[key];
    },
    openModal (props) {
      // Force update the current bolus
      this.clickedId = null;
      if (!this.updateActivationDate.isInProgress && !this.updateCalibration.isInProgress) {
        this.editBolusModalVisible = true;
        this.clickedId = props.item.bolus_id;
      }
    },
    async loadData () {
      try {
        this.loading = true
        this.error = null
        this.boluses = (await BolusApi.list({withBolusPh: true})).data;
        this.filteredBoluses = this.boluses;
        this.loading = false
      } catch (err) {
        this.error = err
        console.log(err)
      }
    },
    async fileSelectOnChange(event) {
      this.bolusesCsv = event.target.files ? event.target.files[0] : null;
      const bolusesJson = await UtilsService.convertCsvToJson(this.bolusesCsv);
      
      const response = await BolusApi.uploadBoluses(bolusesJson);

      if (response.status && response.status == 200) {
        alert('Success!');
        this.loadData()
      } else {
        alert(response.data);
      }
      document.getElementById("fileUpload").value = "";
    },
    chooseFiles() {
        document.getElementById("fileUpload").click()
    },
    setActDateProgress(value) {
      this.updateActivationDate.isInProgress = value;

      if (value === false) {
        this.updateActivationDate.bolusIds = [];
        this.updateActivationDate.activationDate = null;
      }
    },
    setCalibrationProgress(value) {
      this.updateCalibration.isInProgress = value;

      if(!value) {
        this.selected = [];
        this.updateCalibration.bolusIds = [];
        this.updateCalibration.from = null;
        this.updateCalibration.to = null;
        this.error = null;
      }
    },
    addBolus(event, bolus) {
      if (this.updateActivationDate.bolusIds.includes(bolus.bolus_id) && !event.target.checked) {
        this.updateActivationDate.bolusIds =
          this.updateActivationDate.bolusIds.filter(bolusId => bolusId !== bolus.bolus_id);
      } else if (!this.updateActivationDate.bolusIds.includes(bolus.bolus_id) && event.target.checked) {
        this.updateActivationDate.bolusIds.push(bolus.bolus_id);
      }
    },
    async saveNewActDates() {
      const response = await BolusApi.updateActivationDates(
        {
          bolus_ids: this.updateActivationDate.bolusIds,
          activation_date: UtilsService.utcDate(this.updateActivationDate.activationDate)
        }
      );

      if (response.status === 200) {
        this.setActDateProgress(false);
        await this.loadData();
      }
    },
    async saveCalibration() {
      this.server_errors = [];
      const response = await BolusApi.updateCalibration(
        {
          bolus_ids: this.updateCalibration.bolusIds,
          from_date: this.$moment(this.updateCalibration.from).format('YYYY-MM-DDTHH:mm:ssZ'),
          to_date: this.$moment(this.updateCalibration.to).format('YYYY-MM-DDTHH:mm:ssZ')
        }
      );

      if (response.status === 200) {
        if(response.data && response.data.length > 0) {
          this.server_errors = response.data.map(error => `${error.bolus_id}: ${error.message}`);
          return;
        }
        this.setCalibrationProgress(false);
        await this.loadData();
      } else {
        this.error = response.data
      }
    },
    actDateUpdateIsDisabled() {
      // If either of the dates are not set or there are no boluses selected
      if (!this.updateActivationDate.activationDate || this.updateActivationDate.bolusIds.length === 0)
        return true;

      // If the activation date is in the future
      if (this.$refs.UpdateActivationDatePicker.error)
        return true;

      return false;
    },
    calibrationIsDisabled() {
      // If either of the dates are not set or there are no boluses selected
      if(!this.updateCalibration.from || !this.updateCalibration.to || this.updateCalibration.bolusIds.length === 0)
        return true;

      // If the from date is after the to date
      if(this.$refs.UpdateCalibrationFromDatePicker.error || this.$refs.UpdateCalibrationToDatePicker.error)
        return true;

      return false;
    },
    updateSearch(search) {
      this.search = search;
    }
  },
  watch: {
    selected(){
      if(this.updateActivationDate.isInProgress){
        this.updateActivationDate.bolusIds = this.selected.map(bolus => bolus.bolus_id);
      }
      if(this.updateCalibration.isInProgress) {
        this.updateCalibration.bolusIds = this.selected.map(bolus => bolus.bolus_id);
      }
    }
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/Boluses.vue